import { LoadingButton } from "@mui/lab";

import {
  Button,
  Card,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { Form, FormikProvider, useFormik } from "formik";
import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useMutate } from "src/hooks/useFetch.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import {
  remoteOperationTaskUrl,
  remoteOperationUrl,
} from "src/react-query/endPoints";
import { fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { remoteOperationsDateFormat } from "src/utils/formatTime";
import { remoteOperationConfig } from "./remoteOperationConfig";
import { remoteOpsStatusSlice } from "src/store/remoteOpsStatus/remoteOpsStatus.slice";
import { useAuth } from "src/hooks/useAuth.hook";

/** ✅ Normalize any operation name (handles underscores, casing, etc.) */
const normalizeOperation = (op) => {
  if (!op) return "";
  const formatted = op.replace(/[\s-_]/g, "").toLowerCase();
  const map = {
    changeavailability: "ChangeAvailability",
    changeconfiguration: "ChangeConfiguration",
    remotestart: "RemoteStart",
    remotestop: "RemoteStop",
    getdiagnostics: "GetDiagnostics",
    updatefirmware: "UpdateFirmware",
    reset: "Reset",
    triggermessage: "TriggerMessage",
    unlockconnector: "UnlockConnector",
  };
  return map[formatted] || op;
};

export const RemoteOperationForm = ({
  operation,
  initialValues,
  validationSchema,
  onClose,
  chargerId,
  chargerPk,
  queryKeysDropdown,
}) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [dateTimeErr, setDateTimeErr] = useState({ fieldName: "", message: "" });
  const { teamId } = useSelectedTeam();
  const {
    user: { email },
  } = useAuth();

  const normalizedOp = normalizeOperation(operation);

  // Helper function to format date for datetime-local input (same as log tab)
  const formatDateForInput = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date)) {
      return "";
    }
    try {
      // Format as YYYY-MM-DDTHH:MM for datetime-local input
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.warn("Date formatting error:", error);
      return "";
    }
  };

  const results = useQueries({
    queries:
      queryKeysDropdown?.map(({ queryKey: key, usePk, useEmail }) => ({
        queryKey: !useEmail
          ? queryKeys[key](teamId, usePk ? chargerPk : chargerId)
          : queryKeys[key](teamId, usePk ? chargerPk : chargerId, email),
        queryFn: fetchWithError,
        staleTime: 0,
        select: (data) => ({
          data: data?.data,
          key,
        }),
      })) ?? [],
  });

  const dropdownOptions = useMemo(
    () =>
      results?.reduce(
        (acc, curr) => ({
          ...acc,
          [curr?.data?.key]: curr?.data?.data,
        }),
        {}
      ) ?? {},
    [results]
  );

  const onRemoteOperationStatus = (data) => {
    if (onClose) onClose();
    dispatch(
      remoteOpsStatusSlice.actions.setRemoteOps({
        open: true,
        operation,
        data: { chargerId, response: data.data.status },
      })
    );
    resetForm();
    queryKeysDropdown.forEach(({ queryKey: key, usePk }) =>
      queryClient.invalidateQueries(queryKeys[key](teamId, usePk ? chargerPk : chargerId))
    );
  };

  const { mutate, reset, isLoading } = useMutate("chargers", {}, false);

  const onOperationSent = (data) => {
    reset();
    mutate(
      {
        url: remoteOperationTaskUrl(chargerId, data?.data?.taskId),
        method: "GET",
      },
      { onSuccess: onRemoteOperationStatus }
    );
  };

  const onSubmit = (data, { setSubmitting, setErrors }) => {
    let payload = { ...data };
    
    switch (normalizedOp) {
      case "GetDiagnostics":
        payload.start = remoteOperationsDateFormat(payload.start);
        payload.stop = remoteOperationsDateFormat(payload.stop);
        break;
      case "UpdateFirmware":
        payload.retrieveDate = remoteOperationsDateFormat(payload.retrieveDate);
        payload.stop = remoteOperationsDateFormat(payload.stop);
        break;
      case "ChangeConfiguration":
        let errors = { customConfKey: "", confKey: "" };
        if (payload.keyType === "Custom") {
          errors.customConfKey = !payload.customConfKey ? "This field is required" : "";
        } else {
          errors.confKey = !payload.confKey ? "This field is required" : "";
        }
        if (Object.values(errors).includes("This field is required")) {
          setErrors(errors);
          setSubmitting(false);
          return;
        }
        break;
      default:
        break;
    }

    //   parameters: payload,
    //   chargerId,
    //   remoteOperationType: normalizedOp,
    // });

    mutate(
      {
        url: remoteOperationUrl,
        method: "POST",
        body: {
          parameters: payload,
          chargerId,
          remoteOperationType: normalizedOp,
        },
      },
      { onSuccess: onOperationSent }
    );
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
    values,
    resetForm,
  } = formik;

  const allowedDateTime = (currentFieldName, allFieldsArr) => {
    const correspondingFieldName = allFieldsArr.find(
      ({ fieldName, type }) => fieldName !== currentFieldName && type === "DATE/TIME"
    )?.fieldName;
    let allowed;
    switch (currentFieldName) {
      case "start":
      case "retrieveDate":
        allowed = new Date();
        allowed.setMinutes(allowed.getMinutes() - 5);
        break;
      case "stop":
        if (values[correspondingFieldName]) {
          allowed = new Date(values[correspondingFieldName]);
          allowed.setHours(allowed.getHours() + 1);
        } else allowed = new Date();
        break;
      default:
        break;
    }
    return allowed;
  };

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" id="remoteOperations" noValidate onSubmit={handleSubmit}>
        <Card sx={{ p: 2, minHeight: 200 }}>
          <Grid container spacing={2}>
            {(remoteOperationConfig[normalizedOp] || []).map(
              (
                {
                  fieldName,
                  required,
                  type,
                  label,
                  options,
                  queryKey,
                  formatOptions,
                  componentDependentOn,
                },
                index
              ) => {
                const allFieldsArr = remoteOperationConfig[normalizedOp] || [];

                // Check if field should be rendered based on componentDependentOn
                if (componentDependentOn) {
                  const { field, requiredCriteriaValue, disabledCriteriaValue } = componentDependentOn;
                  const dependentFieldValue = values[field];

                  // If the dependent field has a value that should disable this field, don't render it
                  if (disabledCriteriaValue && dependentFieldValue === disabledCriteriaValue) {
                    return null;
                  }

                  // If the dependent field doesn't have the required value, don't render it
                  if (requiredCriteriaValue && dependentFieldValue !== requiredCriteriaValue) {
                    return null;
                  }
                }

                switch (type) {
                  case "TEXT":
                    return (
                      <Grid key={`${fieldName}-${index}`} item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <TextField
                            name={fieldName}
                            required={required}
                            label={label}
                            size="small"
                            {...getFieldProps(fieldName)}
                            error={Boolean(touched[fieldName] && errors[fieldName])}
                            helperText={touched[fieldName] && errors[fieldName]}
                          />
                        </FormControl>
                      </Grid>
                    );

                  case "DROPDOWN":
                    return (
                      <Grid key={`${fieldName}-${index}`} item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          size="small"
                          error={Boolean(touched[fieldName] && errors[fieldName])}
                        >
                          <InputLabel id={fieldName}>{label}</InputLabel>
                          <Select
                            labelId={fieldName}
                            label={label}
                            name={fieldName}
                            value={values[fieldName] || ""}
                            onChange={(event) => setFieldValue(fieldName, event.target.value)}
                            required={required}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {options?.map((item, itemIndex) => (
                              <MenuItem
                                key={`${fieldName}-option-${itemIndex}`}
                                value={item?.value ?? item}
                              >
                                {item?.label ?? item}
                              </MenuItem>
                            ))}
                            {queryKey &&
                              dropdownOptions[queryKey]?.map((item, itemIndex) =>
                                formatOptions({ ...item, key: `${fieldName}-query-${itemIndex}` })
                              )}
                          </Select>
                          {touched[fieldName] && (
                            <FormHelperText>{errors[fieldName]}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    );

                  case "DATE/TIME":
                    return (
                      <Grid item xs={12} md={6} key={`${fieldName}-${index}`}>
                        <FormControl fullWidth size="small">
                          <TextField
                            name={fieldName}
                            required={required}
                            label={label}
                            type="datetime-local"
                            size="small"
                            value={values[fieldName] ? formatDateForInput(values[fieldName]) : ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFieldValue(fieldName, value ? new Date(value) : "");
                            }}
                            error={Boolean(
                              (touched[fieldName] && errors[fieldName]) ||
                                (touched[fieldName] &&
                                  dateTimeErr.fieldName === fieldName &&
                                  dateTimeErr.message)
                            )}
                            helperText={
                              (dateTimeErr.fieldName === fieldName && dateTimeErr.message
                                ? dateTimeErr.message
                                : "") ||
                              (touched[fieldName] && errors[fieldName])
                            }
                            InputLabelProps={{
                              shrink: true,
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#1976d2",
                                  borderWidth: 2,
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#1976d2",
                                  borderWidth: 2,
                                },
                              },
                              "& .MuiInputLabel-root": {
                                fontWeight: 600,
                                color: "#1976d2",
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#1976d2",
                              },
                            }}
                          />
                        </FormControl>
                      </Grid>
                    );
                  default:
                    return null;
                }
              }
            )}
          </Grid>
        </Card>

        <Grid container mt={4} justifyContent="end">
          <Grid item xs={6} lg={6}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <LoadingButton
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting || isLoading}
                loadingIndicator="Sending..."
              >
                Send Message
              </LoadingButton>
            </Stack>
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  );
};